// UserProfile.jsx - Full Page Component with View/Edit Mode
import React, { useState, useEffect, useRef } from 'react';
import { Upload, Check, AlertCircle, Edit, X, ArrowLeft } from 'lucide-react';
import { useTheme } from '../Theme/ThemeProvider';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { setUserDetails } from '../../store/userSlice';
import uploadImage from '../../helpers/uploadImage'; // Import the upload helper
import { authFetch } from 'helpers/authFetch';


const UserProfile = () => {
  const { colors } = useTheme();
  const user = useSelector((state) => state?.user?.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    address: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});

const getFullImageUrl = (user) => {
  if (!user) return null;
  
  // Use picturePath instead of profilePic
  const picturePath = user.picturePath;
  
  if (!picturePath) return null;
  
  // If it's already a Cloudinary URL or full URL, return as is
  if (picturePath.startsWith('http')) {
    return picturePath;
  }
  
  // If it's a local upload path, construct the full URL
  const cleanPath = picturePath.replace(/\\/g, '/').replace(/^\/+/, '');
  return `${process.env.REACT_APP_BACKEND_URL}/${cleanPath}`;
};

const profilePicUrl = getFullImageUrl(user);

// Debug logs
console.log("User picturePath:", user?.picturePath);
console.log("Constructed URL:", profilePicUrl);


  const fetchProfile = async () => {
    if (!user?._id) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      const url = `${summaryApi.getProfileDetails.url}/${user._id}`;
      console.log("Fetching from URL:", url);

      const response = await authFetch(url, {
        method: summaryApi.getProfileDetails.method,
        
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Profile fetched:', data);
      
      if (data.success) {
        setProfile(data.data);
        const fullImageUrl = getFullImageUrl(data?.data);
        setPreviewImage(fullImageUrl);
        setFormData({
          address: data.data.address || '',
          phone: data.data.phone || ''
        });
      } else {
        throw new Error(data.message || 'Failed to fetch profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError(error.message || 'Failed to load profile. Please try again later.');
      toast.error('Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user?._id]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.length > 200) {
      newErrors.address = 'Address must be less than 200 characters';
    }

    const phoneRegex = /^[0-9]{10,16}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Phone number must be between 10 and 16 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      setUploadingImage(true);

      try {
        // Upload to Cloudinary using the same helper as RepairRequestForm
        const uploadedImage = await uploadImage(file);
        
        if (uploadedImage && uploadedImage.secure_url) {
          // Set the Cloudinary URL directly
          setPreviewImage(uploadedImage.secure_url);
          // Store the Cloudinary URL instead of the file object
          setProfileImage(uploadedImage.secure_url);
          toast.success('Profile picture uploaded successfully');
        } else {
          throw new Error('Failed to upload image to Cloudinary');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image. Please try again.');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);

    try {
        // Prepare the data to send
        const updateData = {
            userId: user._id,
            address: formData.address,
            phone: formData.phone
        };

        // If we have a new Cloudinary image URL, include it
        if (profileImage && typeof profileImage === 'string' && profileImage.startsWith('http')) {
            updateData.profilePic = profileImage; // Send Cloudinary URL
            console.log("Sending Cloudinary URL:", profileImage);
        }

        const response = await authFetch(`${summaryApi.updateProfileDetails.url}/${user._id}`, {
            method: summaryApi.updateProfileDetails.method,
            
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
            const updatedUser = {
                ...user,
                ...result.data,
                picturePath: result.data.picturePath
            };
            dispatch(setUserDetails(updatedUser));
            toast.success(result.message || "Profile updated successfully");
            setIsEditMode(false);
            fetchProfile(); // Refresh profile data
        } else {
            throw new Error(result.message || "Update failed");
        }
    } catch (error) {
        console.error('Profile update failed:', error);
        toast.error(error.message || "Failed to update profile");
    } finally {
        setSubmitting(false);
    }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setProfileImage(null);
    setPreviewImage(getFullImageUrl(profile?.profilePic));
    setFormData({
      address: profile?.address || '',
      phone: profile?.phone || ''
    });
    setErrors({});
  };

  console.log("UserProfile Debug:");
console.log("previewImage:", previewImage);
console.log("profile?.profilePic:", profile?.profilePic);
console.log("user?.picturePath:", user?.picturePath);

  if (loading) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <div 
          className="w-full max-w-2xl rounded-2xl shadow-2xl p-8"
          style={{ backgroundColor: colors.bg }}
        >
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div 
                className="animate-spin rounded-full h-12 w-12 border-4 border-transparent mx-auto"
                style={{
                  borderTopColor: colors.primary,
                  borderRightColor: `${colors.primary}40`,
                  borderBottomColor: `${colors.primary}40`,
                }}
              />
              <p className="mt-4 text-sm" style={{ color: colors.textSecondary }}>Loading your profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <div 
          className="w-full max-w-2xl rounded-2xl shadow-2xl p-8"
          style={{ backgroundColor: colors.bg }}
        >
          <div className="text-center py-8">
            <div 
              className="p-6 rounded-xl inline-flex flex-col items-center gap-3"
              style={{
                backgroundColor: `${colors.accent}15`,
                border: `1px solid ${colors.accent}30`,
              }}
            >
              <AlertCircle size={24} style={{ color: colors.accent }} />
              <p style={{ color: colors.accent }} className="text-sm">{error}</p>
            </div>
            <button 
              onClick={() => {
                setError(null);
                fetchProfile();
              }}
              className="mt-4 px-6 py-2 rounded-lg font-medium transition-all hover:scale-105"
              style={{
                backgroundColor: colors.primary,
                color: 'white'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <div 
          className="w-full max-w-2xl rounded-2xl shadow-2xl p-8"
          style={{ backgroundColor: colors.bg }}
        >
          <div className="text-center py-8">
            <p style={{ color: colors.textSecondary }}>No profile found</p>
            <button 
              onClick={handleClose}
              className="mt-4 px-6 py-2 rounded-lg font-medium transition-all hover:scale-105"
              style={{
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                border: `1px solid ${colors.border}`
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="w-full max-w-2xl rounded-2xl shadow-2xl transform transition-all duration-300 ease-in-out"
        style={{
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: 'auto'
        }}
      >
        {/* Header with Close Button */}
        <div 
          className="sticky top-0 flex items-center justify-between p-6 border-b"
          style={{ 
            borderColor: colors.border,
            backgroundColor: colors.bg
          }}
        >
          <h2 
            className="text-2xl font-bold"
            style={{ color: colors.primary }}
          >
            {isEditMode ? 'Edit Profile' : 'My Profile'}
          </h2>
          <button 
            onClick={handleClose}
            className="p-2 rounded-lg transition-all hover:scale-110"
            style={{
              backgroundColor: `${colors.primary}15`,
              color: colors.primary,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* View Mode */}
          {!isEditMode ? (
            <div className="space-y-6">
              {/* Profile Picture Section */}
              <div className="text-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative group">
                    {previewImage || profile?.profilePic ?  (
                      <img 
                        src={previewImage || profile?.profilePic} 
                        alt="Profile"
                        className="h-40 w-40 object-cover rounded-2xl shadow-xl"
                        onError={(e) => {
            // Handle broken images
            console.error("Image failed to load:", e.target.src);
            e.target.style.display = 'none';
          }}
                      />
                    ) : (
                      <div 
                        className="h-40 w-40 rounded-2xl flex items-center justify-center text-5xl font-bold text-white shadow-lg"
                        style={{ backgroundColor: colors.primary }}
                      >
                        {profile?.firstName?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                  <p style={{ color: colors.textSecondary }} className="text-sm">
                    {profile?.firstName} {profile?.lastName}
                  </p>
                </div>
              </div>

              {/* Personal Info */}
              <div>
                <h3 
                  className="text-lg font-semibold mb-4 flex items-center gap-2"
                  style={{ color: colors.text }}
                >
                  <div className="h-1 w-1 rounded-full" style={{ backgroundColor: colors.primary }} />
                  Personal Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                      First Name
                    </label>
                    <p style={{ color: colors.text }} className="text-base font-medium">
                      {profile?.firstName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                      Last Name
                    </label>
                    <p style={{ color: colors.text }} className="text-base font-medium">
                      {profile?.lastName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                      Email
                    </label>
                    <p style={{ color: colors.text }} className="text-base font-medium">
                      {profile?.email || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 
                  className="text-lg font-semibold mb-4 flex items-center gap-2"
                  style={{ color: colors.text }}
                >
                  <div className="h-1 w-1 rounded-full" style={{ backgroundColor: colors.accent }} />
                  Contact Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                      Address
                    </label>
                    <p style={{ color: colors.text }} className="text-base font-medium">
                      {profile?.address || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                      Phone Number
                    </label>
                    <p style={{ color: colors.text }} className="text-base font-medium">
                      {profile?.phone || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Edit Button */}
              <div className="flex gap-3 pt-6 border-t" style={{ borderColor: colors.border }}>
                <button 
                  onClick={() => setIsEditMode(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105 active:scale-95"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Edit size={18} />
                  Edit Profile
                </button>
                <button 
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 active:scale-95"
                  style={{
                    backgroundColor: colors.bgTertiary,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <div className="space-y-6">
              {/* Profile Picture Edit */}
              <div className="text-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative group">
                     {previewImage || profile?.profilePic ? (
                      <img 
                        src={previewImage || profile?.profilePic}
                        alt="Profile"
                        className="h-40 w-40 object-cover rounded-2xl shadow-xl transition-all group-hover:shadow-2xl"
                      />
                    ) : (
                      <div 
                        className="h-40 w-40 rounded-2xl flex items-center justify-center text-5xl font-bold text-white shadow-lg"
                        style={{ backgroundColor: colors.primary }}
                      >
                        {profile?.firstName?.charAt(0) || 'U'}
                      </div>
                    )}
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-2xl flex items-center justify-center">
                        <div 
                          className="animate-spin rounded-full h-8 w-8 border-4 border-transparent"
                          style={{
                            borderTopColor: colors.primary,
                            borderRightColor: `${colors.primary}40`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer font-medium transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: `${colors.primary}15`,
                      color: colors.primary,
                      border: `2px solid ${colors.primary}40`,
                    }}
                  >
                    <Upload size={18} />
                    <span>{uploadingImage ? 'Uploading...' : 'Change Picture'}</span>
                  </button>
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden" 
                    disabled={uploadingImage}
                  />
                </div>
              </div>

              {/* Editable Info */}
              <div>
                <h3 
                  className="text-lg font-semibold mb-4 flex items-center gap-2"
                  style={{ color: colors.text }}
                >
                  <div className="h-1 w-1 rounded-full" style={{ backgroundColor: colors.accent }} />
                  Update Information
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label 
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.text }}
                    >
                      Address *
                    </label>
                    <input 
                      type="text" 
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter your address"
                      className="w-full px-4 py-2.5 rounded-lg transition-all focus:outline-none"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${errors.address ? colors.accent : colors.border}`,
                        boxShadow: errors.address ? `0 0 0 3px ${colors.accent}20` : 'none',
                      }}
                    />
                    {errors.address && (
                      <div className="flex items-center gap-2 mt-2" style={{ color: colors.accent }}>
                        <AlertCircle size={16} />
                        <span className="text-sm">{errors.address}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.text }}
                    >
                      Phone Number *
                    </label>
                    <input 
                      type="text" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-2.5 rounded-lg transition-all focus:outline-none"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${errors.phone ? colors.accent : colors.border}`,
                        boxShadow: errors.phone ? `0 0 0 3px ${colors.accent}20` : 'none',
                      }}
                    />
                    {errors.phone && (
                      <div className="flex items-center gap-2 mt-2" style={{ color: colors.accent }}>
                        <AlertCircle size={16} />
                        <span className="text-sm">{errors.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div 
                className="flex flex-col sm:flex-row gap-3 pt-6 border-t"
                style={{ borderColor: colors.border }}
              >
                <button 
                  onClick={handleSubmit}
                  disabled={submitting || uploadingImage}
                  className="flex-1 sm:flex-auto flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed active:scale-95"
                  style={{ backgroundColor: colors.primary }}
                >
                  {submitting ? (
                    <>
                      <div 
                        className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" 
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Save Changes
                    </>
                  )}
                </button>
                
                <button 
                  type="button" 
                  onClick={handleCancel}
                  disabled={submitting}
                  className="flex-1 sm:flex-auto px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: colors.bgTertiary,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;